/*
 * Getting started: which setup steps are done, derived only from what the API returns (no local "mark as done").
 * Six steps, all tracked: build providers (Expo EAS, Bitrise once the API runs it), projects, apps, build profiles,
 * env vars and the first build (Run build in the console). The CLI & CI access step returns with docs/mvp-plan.md P17.
 * A fact the page could not read yet is `undefined`: its step shows "checking" and never counts. Pure module (no React,
 * no imports), so it can be checked on its own.
 */

export type StepId = 'provider' | 'project' | 'app' | 'profile' | 'env' | 'build';
/** `attention`: done, with something to fix (counts as done, like the design's "Done*"). */
export type StepStatus = 'done' | 'attention' | 'todo' | 'blocked' | 'checking';

/** What the page reads from the API for the selected project and app. */
export interface SetupFacts {
  /** Provider accounts builds can run on: active EAS accounts, and active Bitrise ones once the API builds on Bitrise. */
  providerAccounts: number;
  projects: number;
  /** Live apps of the selected project. */
  apps: number;
  /** Live profiles of the selected app. */
  profiles: number;
  /** Profiles Run build can start: `hangar_runner` (Expo), or `provider_api` on Bitrise once the API runs it. */
  runnableProfiles: number;
  /** Keys that resolve for the selected app in its busiest environment; undefined = not read (yet, or no env:read). */
  envKeys: number | undefined;
  /** Required keys missing, per environment. */
  envMissing: Array<{ key: string; environment: string }>;
  /** Builds of the selected app; undefined = not read. */
  builds: number | undefined;
  buildsSucceeded: number;
}

export interface StepState {
  id: StepId;
  status: StepStatus;
  /** The step that has to be done first (only when `blocked`). */
  blockedBy?: StepId;
}

export const STEP_ORDER: StepId[] = ['provider', 'project', 'app', 'profile', 'env', 'build'];
export const stepNumber = (id: StepId): number => STEP_ORDER.indexOf(id) + 1;

export const STEP_TITLE: Record<StepId, string> = {
  provider: 'Build service',
  project: 'Projects',
  app: 'Apps',
  profile: 'Build profiles',
  env: 'Environment variables',
  build: 'First build',
};

/** The vertical stepper's labels (design 27: "5. Env variables"). */
export const STEP_STEPPER: Record<StepId, string> = { ...STEP_TITLE, env: 'Env variables' };

/** Short labels for the horizontal stepper (tablets). */
export const STEP_SHORT: Record<StepId, string> = { provider: 'Service', project: 'Projects', app: 'Apps', profile: 'Profiles', env: 'Env vars', build: 'First build' };

/** What "Next" asks for: the step's action when it is to do, its fix when it needs attention. */
export const NEXT_ACTION: Record<StepId, { todo: string; attention: string }> = {
  provider: { todo: 'Get your build service ready', attention: 'Check your build service' },
  project: { todo: 'Create a project', attention: 'Check your project' },
  app: { todo: 'Connect your app', attention: 'Check your app' },
  profile: { todo: 'Create a build profile', attention: 'Make a profile runnable' },
  env: { todo: 'Add environment variables', attention: 'Add the missing variables' },
  build: { todo: 'Run your first build', attention: 'Get a build to succeed' },
};

/** The mono status label of the stepper ("Done", "Done*", "Needs step 6"…). */
export function statusLabel(s: StepState): string {
  switch (s.status) {
    case 'done':
      return 'Done';
    case 'attention':
      return 'Done*';
    case 'blocked':
      return `Needs step ${stepNumber(s.blockedBy ?? s.id)}`;
    case 'checking':
      return 'Checking';
    default:
      return 'To do';
  }
}

/** `true` done, `false` not done, `undefined` unknown. */
const DONE: Record<StepId, (f: SetupFacts) => boolean | undefined> = {
  provider: (f) => f.providerAccounts > 0,
  project: (f) => f.projects > 0,
  app: (f) => f.apps > 0,
  profile: (f) => f.profiles > 0,
  env: (f) => (f.envKeys === undefined ? undefined : f.envKeys > 0),
  build: (f) => (f.builds === undefined ? undefined : f.builds > 0),
};

/** Done, but with something to fix. */
const ATTENTION: Partial<Record<StepId, (f: SetupFacts) => boolean>> = {
  profile: (f) => f.runnableProfiles === 0,
  env: (f) => f.envMissing.length > 0,
  build: (f) => f.buildsSucceeded === 0,
};

/** What each step needs before it can be done (the first missing one wins). */
const NEEDS: Partial<Record<StepId, StepId[]>> = {
  app: ['project'],
  profile: ['app', 'provider'],
  env: ['app'],
  build: ['profile'],
};

export function stepStates(f: SetupFacts): StepState[] {
  return STEP_ORDER.map((id) => {
    const done = DONE[id](f);
    if (done) return { id, status: ATTENTION[id]?.(f) ? 'attention' : 'done' };
    const missing = (NEEDS[id] ?? []).find((dep) => DONE[dep](f) !== true);
    if (missing) return { id, status: 'blocked', blockedBy: missing };
    return { id, status: done === undefined ? 'checking' : 'todo' };
  });
}

export const isDone = (s: StepState): boolean => s.status === 'done' || s.status === 'attention';

export interface Progress {
  done: number;
  tracked: number;
  /** Whole percent, for the bar and the runbook. */
  percent: number;
  /** The first step to do; else the first one that needs attention; undefined when everything is done. */
  next: StepId | undefined;
}

export function progressOf(states: StepState[]): Progress {
  const done = states.filter(isDone).length;
  const next = states.find((s) => s.status === 'todo')?.id ?? states.find((s) => s.status === 'attention')?.id;
  return { done, tracked: states.length, percent: Math.round((done / Math.max(1, states.length)) * 100), next };
}

/** What the readiness rail and the runbook print: one line per check, passed or missing. */
export interface ReadinessItem {
  key: string;
  step: StepId;
  ok: boolean;
  label: string;
  detail?: string;
}

/** Names the labels need (the selected project and app, the signed-in person). */
export interface SetupNames {
  /** The build service accounts, by name: platform operators only (empty for clients, SPEC decision 23). */
  accounts: string[];
  project?: string;
  app?: string;
  person?: string;
}

const item = (key: string, step: StepId, ok: boolean, label: string, detail?: string): ReadinessItem => (detail === undefined ? { key, step, ok, label } : { key, step, ok, label, detail });

/** The build service line: operators read the account names; clients (no names passed) only that it is ready. */
function providerItem(f: SetupFacts, n: SetupNames): ReadinessItem {
  if (f.providerAccounts > 0) return item('provider', 'provider', true, n.accounts.length ? `Build service connected (${n.accounts.join(', ')})` : 'Build service ready');
  return item('provider', 'provider', false, 'Build service', 'Your Hangar administrators set up the build service for your organization.');
}

function appItem(f: SetupFacts, n: SetupNames, app: string): ReadinessItem {
  if (f.projects > 0 && f.apps > 0) return item('app', 'app', true, `Project & app registered (${n.project ?? '—'} · ${app})`);
  if (f.projects > 0) return item('app', 'app', false, 'App', `Connect an app to ${n.project ?? 'the project'}.`);
  return item('app', 'project', false, 'Project', 'Create the project your apps live in.');
}

/** The profile line, plus (once there is a profile) whether one of them can start builds. */
function profileItems(f: SetupFacts, app: string): ReadinessItem[] {
  if (f.profiles === 0) return [item('profile', 'profile', false, 'Build profile', `No build profile for ${app} yet.`)];
  const runnable =
    f.runnableProfiles > 0
      ? item('runnable', 'profile', true, 'A profile can start builds with Run build')
      : item('runnable', 'profile', false, 'Runnable profile', 'No profile can start builds from Run build yet.');
  return [item('profile', 'profile', true, `Build profiles created (${f.profiles})`), runnable];
}

/** Env vars resolving (once read), then one line per required key that is missing. */
function envItems(f: SetupFacts, app: string): ReadinessItem[] {
  const out: ReadinessItem[] = [];
  if (f.envKeys !== undefined) {
    out.push(f.envKeys > 0 ? item('env', 'env', true, `Env vars resolve (${f.envKeys} ${f.envKeys === 1 ? 'key' : 'keys'})`) : item('env', 'env', false, 'Env vars', `No variable resolves for ${app} yet.`));
  }
  for (const m of f.envMissing) out.push(item(`missing-${m.environment}-${m.key}`, 'env', false, `${m.key} in ${m.environment}`, 'Required and missing: a build there is refused.'));
  return out;
}

function buildItems(f: SetupFacts): ReadinessItem[] {
  if (f.builds === undefined) return [];
  if (f.buildsSucceeded > 0) return [item('build', 'build', true, `A build succeeded (${f.builds} ${f.builds === 1 ? 'build' : 'builds'} so far)`)];
  return [item('build', 'build', false, 'First build', f.builds > 0 ? 'No build has succeeded yet.' : `Use Run build in step ${stepNumber('build')}.`)];
}

/** The checks behind the rail, from the same facts as the steps. */
export function readinessOf(f: SetupFacts, n: SetupNames): ReadinessItem[] {
  const app = n.app ?? 'the app';
  return [providerItem(f, n), appItem(f, n, app), ...profileItems(f, app), ...envItems(f, app), ...buildItems(f)];
}

/** True when the API base URL points at this machine, so provider webhooks need a tunnel to reach it. */
export const isLocalApi = (apiUrl: string): boolean => {
  try {
    return ['localhost', '127.0.0.1', '[::1]'].includes(new URL(apiUrl).hostname);
  } catch {
    return false;
  }
};

/** The first environment a profile builds for (in the project's order), else staging. */
export function environmentOf(environments: Array<{ id: string; slug: string; sortOrder: number }>, boundIds: string[]): string {
  const envs = [...environments].sort((a, b) => a.sortOrder - b.sortOrder);
  return envs.find((e) => boundIds.includes(e.id))?.slug ?? envs.find((e) => e.slug === 'staging')?.slug ?? envs[0]?.slug ?? 'staging';
}
